import React, { useContext } from "react";
import styles from "../side.module.scss";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../../../../constant/google-analytics/GAEvents";
import { sideBarData } from "../../../../../constant/sideMenuOptions";
import ProfileMenu from "../sideBarProfile/sideBarProfile";
import ProfileDetails from "../profile/profile";
import SideMenuDropDown from "../../../../reusableComponent/sideMenuDropDown/sideMenuDropDown";
import ChatHistoryPanel from "../../../../reusableComponent/sideBar/history/ChatHistoryPanel";
import { logBookConversion } from "../../../../../experiments";
import { isAppEmbedFlow } from "../../../../../utils/embedPortalBridge";
import { isAdminUser } from "../../../../../utils/isAdminUser";
import { StateContext } from "../../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../../context/globalContext/interface";
import { brandLabel, UiLabelKey } from "../../../../../utils/partnerBranding";

/** Rail item -> the partner-renameable label it shows; `label` in sideBarData is the default. */
const RAIL_LABEL_KEYS: Record<string, UiLabelKey> = { chat: "navChat", book: "navShop", myTrips: "navTrips" };

interface NewSideBarContentProps {
  isMobile: boolean;
  isDesktopSidebarExpand: boolean;
  toggleSidebar: () => void;
  setIsOpenMobileSidebar: (isOpen: boolean) => void;
  setIsAdminChat: (isAdmin: boolean) => void;
  setCurrentHistoryId: (id: string) => void;
  handleNav: () => void;
  handleMobNav: () => void;
  userInfo: any;
  firstScreen: string;
  screen: string;
  handleSelect: (value: string) => void;
}

const NewSideBarContent: React.FC<NewSideBarContentProps> = ({
  isMobile,
  isDesktopSidebarExpand,
  toggleSidebar,
  setIsOpenMobileSidebar,
  setIsAdminChat,
  setCurrentHistoryId,
  handleNav,
  handleMobNav,
  userInfo,
  firstScreen,
  screen,
  handleSelect,
}) => {
  const { branding }: iGlobalContext = useContext(StateContext);
  const isAdmin = isAdminUser(userInfo);
  const EMBED_MENU_SCREENS = ["book", "myTrips", "history"];
  const railItems = isAppEmbedFlow()
    ? sideBarData.filter((item) => EMBED_MENU_SCREENS.includes(item.screenName))
    : sideBarData;

  const startNewChat = () => {
    triggerGAEvent(GA_LABEL_ENUMS.new_trip_click);
    setIsAdminChat(false);
    if (isMobile) handleMobNav();
    else handleNav();
  };

  const startPublicChat = () => {
    triggerGAEvent(GA_LABEL_ENUMS.new_trip_public_click);
    setCurrentHistoryId("");
    setIsAdminChat(true);
    if (isMobile) handleMobNav();
    else handleNav();
  };

  if (isMobile) {
    return (
      <div className={styles.mobileDrawerCard}>
        <ChatHistoryPanel
          variant="mobileDrawer"
          onNewChat={startNewChat}
          showPublicChat={isAdmin}
          onPublicChat={startPublicChat}
          setIsOpenMobileSidebar={setIsOpenMobileSidebar}
        />
      </div>
    );
  }

  const isShopSelected = screen === "newBooking";
  const isTripsSelected = !isShopSelected && firstScreen === "myTrips";
  const isChatSelected =
    !isShopSelected &&
    !isTripsSelected &&
    (firstScreen === "newTrip" || firstScreen === "topDestination" || firstScreen === "");

  const selectedByScreen: Record<string, boolean> = {
    chat: isChatSelected,
    book: isShopSelected,
    myTrips: isTripsSelected,
  };

  const handleRailSelect = (screenName: string) => {
    if (screenName === "chat") {
      startNewChat();
    } else if (screenName === "book") {
      logBookConversion("sidebar");
      handleSelect("Shop travel");
    } else if (screenName === "myTrips") {
      handleSelect("My Trips");
    }
  };

  return (
    <div className={styles.subSideBar}>
      <div className={styles.railMenu}>
        <div className={styles.railTop}>
          <ProfileDetails
            mode="mascot"
            onClick={toggleSidebar}
            handleClick={() => {}}
            isLoggedIn={userInfo?.loggedIn}
            userName={userInfo?.name}
            isDesktopSidebarExpand={isDesktopSidebarExpand}
          />
        </div>
        <div className={styles.railNav}>
          {railItems.map((item, index) => (
            <SideMenuDropDown
              key={index}
              icon={item.icon}
              label={RAIL_LABEL_KEYS[item.screenName] ? brandLabel(branding, RAIL_LABEL_KEYS[item.screenName]) : item.label}
              isSelected={!!selectedByScreen[item.screenName]}
              onSelect={() => handleRailSelect(item.screenName)}
            />
          ))}
        </div>
        {userInfo?.loggedIn && (
          <div className={styles.railFooter}>
            <ProfileMenu
              toggleSidebar={toggleSidebar}
              setIsOpenMobileSidebar={setIsOpenMobileSidebar as any}
              isDesktopSidebarExpand={isDesktopSidebarExpand}
              mode="avatar"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NewSideBarContent;
